import React, { useState } from 'react';
import Modal from 'react-modal';
import { Calendar, X } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { extendTrial } from '../../lib/adminQueries';

interface ExtendTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  currentTrialEnd: string | null;
  onSuccess?: () => void;
}

Modal.setAppElement('#root');

export const ExtendTrialModal: React.FC<ExtendTrialModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  currentTrialEnd,
  onSuccess,
}) => {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const handleExtend = async () => {
    if (days <= 0) {
      showToast('Please enter a valid number of days', 'error');
      return;
    }

    setLoading(true);
    try {
      // TODO: Replace with actual admin user ID from auth context
      // Example: const { currentUser } = useAuth(); const adminUserId = currentUser.id;
      await extendTrial(organizationId, days, 'admin-user-id');
      showToast(`Trial extended by ${days} days for ${organizationName}`, 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error extending trial:', error);
      showToast('Failed to extend trial. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateNewEndDate = () => {
    const current = currentTrialEnd ? new Date(currentTrialEnd) : new Date();
    const newDate = new Date(current.getTime() + days * 24 * 60 * 60 * 1000);
    return newDate.toLocaleDateString();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto mt-20 p-6 outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Extend Trial</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="mb-6">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Extend trial period for <span className="font-semibold">{organizationName}</span>
        </p>

        {currentTrialEnd && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Current trial end: <span className="font-medium">{new Date(currentTrialEnd).toLocaleDateString()}</span>
            </p>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="days" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Number of days to add
          </label>
          <input
            type="number"
            id="days"
            min="1"
            max="365"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value) || 0)}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-start">
          <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-200">New trial end date</p>
            <p className="text-sm text-blue-700 dark:text-blue-300">{calculateNewEndDate()}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleExtend}
          disabled={loading || days <= 0}
          className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Extending...' : 'Extend Trial'}
        </button>
      </div>
    </Modal>
  );
};
